import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FiUsers, FiUserPlus } from 'react-icons/fi';
import { getNameInitials } from 'helpers';
import { EMAIL_REGEX } from 'constants';
import Styles from './CampaignShareModal.module.scss';
import { getUsers } from 'services/userService';
import { createProjectUser, deleteProjectUser } from 'services/projectService';

const selectStyles = {
  option: (styles, { isSelected, isFocused }) => {
    return {
      ...styles,
      padding: '10px 14px',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: "'Inter', sans-serif",
      color: isSelected ? '#00a7b7' : '#344054',
      backgroundColor: isFocused ? '#F9FAFB' : '#FFFFFF',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      borderLeft: isSelected ? '3px solid #00a7b7' : '3px solid transparent',

      '&:active': {
        backgroundColor: '#F2F4F7',
      },
    };
  },

  control: (baseStyles, { isFocused }) => {
    return {
      ...baseStyles,
      minHeight: '44px',
      borderRadius: '8px',
      border: `1px solid ${isFocused ? '#00a7b7' : '#D0D5DD'}`,
      boxShadow: isFocused ? '0 0 0 3px rgba(0, 167, 183, 0.1)' : 'none',
      cursor: 'pointer',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      transition: 'all 0.2s ease',

      '&:hover': {
        borderColor: '#00a7b7',
      },
    };
  },

  menu: (baseStyles) => {
    return {
      ...baseStyles,
      borderRadius: '8px',
      boxShadow:
        '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
      border: '1px solid #E4E7EC',
      overflow: 'hidden',
      marginTop: '4px',
    };
  },

  menuList: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '4px',
    };
  },

  singleValue: (baseStyles) => {
    return {
      ...baseStyles,
      color: '#344054',
      fontWeight: '500',
      fontFamily: "'Inter', sans-serif",
    };
  },

  placeholder: (baseStyles) => {
    return {
      ...baseStyles,
      color: '#98A2B3',
      fontFamily: "'Inter', sans-serif",
    };
  },

  dropdownIndicator: (baseStyles, { isFocused }) => {
    return {
      ...baseStyles,
      color: isFocused ? '#00a7b7' : '#667085',
      transition: 'all 0.2s ease',

      '&:hover': {
        color: '#00a7b7',
      },
    };
  },

  indicatorSeparator: () => {
    return {
      display: 'none',
    };
  },
};

const CampaignShareModal = ({ campaign, editors, currentUser, refetch, ...props }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [email, setEmail] = useState('');
  const [collaborators, setCollaborators] = useState(editors || []);
  const [campaignOwner, setCampaignOwner] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [justAddedId, setJustAddedId] = useState(null);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const response = await getUsers('', 1);
      const { data } = response || {};
      const { users } = data || {};

      const members = (users || []).map((member) => ({
        value: member.id,
        label: member.name || member.email,
        email: member.email,
        avatar: member.mini_avatar,
        userId: member.id,
      }));

      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  }, []);

  useEffect(() => {
    if (props.show && campaign?.id) {
      fetchTeamMembers();
      // Set collaborators from editors prop
      setCollaborators(editors || []);
      // Find the owner
      const owner = editors?.find((e) => e.owner);
      setCampaignOwner(owner);
    }
  }, [props.show, campaign?.id, editors, fetchTeamMembers]);

  const handleAddCollaborator = async () => {
    // Validate input
    if (!selectedMember && !email) {
      toast.warning('Please select a team member or enter an email');
      return;
    }

    if (email && !EMAIL_REGEX.test(email)) {
      toast.warning('Please enter a valid email address');
      return;
    }

    if (selectedMember && selectedMember.userId === campaignOwner?.id) {
      toast.warning('This user is already the owner of this campaign');
      return;
    }

    // Check if already a collaborator (for team members)
    if (selectedMember) {
      const alreadyAdded = collaborators.some(
        (collab) => collab.id === selectedMember.userId
      );
      if (alreadyAdded) {
        toast.warning('This team member already has access to this campaign');
        return;
      }
    }

    // Check if email already added (for email invites)
    if (email) {
      const alreadyAdded = collaborators.some(
        (collab) => collab.email === email
      );
      if (alreadyAdded) {
        toast.warning('This email already has access to this campaign');
        return;
      }
    }

    setIsLoading(true);

    const addedUserId = selectedMember?.userId;
    const addedUserName = selectedMember?.label || email;

    try {
      await createProjectUser(campaign.id, {
        project_user: {
          ...(email ? { user_email: email } : { user_id: addedUserId }),
        },
      });

      // For team members, do immediate frontend update
      if (selectedMember) {
        const newCollaborator = {
          id: addedUserId,
          name: addedUserName,
          email: selectedMember.email,
          avatar: selectedMember.avatar,
          owner: false,
        };

        setCollaborators((prevCollaborators) => {
          const newCollabs = [...prevCollaborators, newCollaborator];
          return newCollabs;
        });

        // Highlight the newly added collaborator
        setJustAddedId(addedUserId);
        setTimeout(() => setJustAddedId(null), 2000);
      }

      toast.success(
        email
          ? `Invitation sent to ${email}!`
          : `${addedUserName} has been added to this campaign!`,
        {
          autoClose: 3000,
        }
      );

      setSelectedMember(null);
      setEmail('');

      // Refetch to sync with backend
      if (refetch) refetch();
    } catch (error) {
      console.error('Error adding collaborator:', error);

      // Try to refetch despite error
      if (refetch) await refetch();

      const errorMessage =
        error?.response?.data?.error || 'Failed to add collaborator';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCollaborator = async (collaboratorId) => {
    if (!window.confirm('Remove this collaborator from the campaign?')) return;

    try {
      await deleteProjectUser(campaign.id, collaboratorId);

      // IMMEDIATE frontend update
      setCollaborators((prevCollaborators) =>
        prevCollaborators.filter((collab) => collab.id !== collaboratorId)
      );

      toast.success('Collaborator removed successfully');

      // Refetch to sync with backend
      if (refetch) refetch();
    } catch (error) {
      console.error('Error removing collaborator:', error);
      toast.error('Failed to remove collaborator');
    }
  };

  // Filter team members to exclude current user, owner, and existing collaborators
  const availableMembers = teamMembers.filter((member) => {
    const isCurrentUser = member.userId === currentUser?.id;
    const isOwner = member.userId === campaignOwner?.id;
    const isCollaborator = collaborators.some(
      (collab) => collab.id === member.userId
    );
    return !isCurrentUser && !isOwner && !isCollaborator;
  });

  return (
    <Modal
      {...props}
      size="md"
      aria-labelledby="campaign-share-modal"
      centered
      dialogClassName={Styles.shareModal}
      style={{ zIndex: 10000 }}
    >
      <div className={Styles.modalHeader}>
        <div className={Styles.headerIcon}>
          <FiUsers size={20} />
        </div>
        <h2>Share Campaign: {campaign?.name || 'Untitled'}</h2>
      </div>

      <Modal.Body className={Styles.modalBody}>
        {/* Add Team Member Section */}
        <div className={Styles.addMemberSection}>
          <label className={Styles.label}>Add team member</label>
          <div className={Styles.inputWrapper}>
            <div className={Styles.selectWrapper}>
              <Select
                isSearchable={true}
                styles={selectStyles}
                options={availableMembers}
                placeholder="Select a team member..."
                value={selectedMember}
                onChange={(value) => {
                  setSelectedMember(value);
                  setEmail('');
                }}
                isDisabled={isLoading}
                noOptionsMessage={() =>
                  availableMembers.length === 0
                    ? 'All team members already have access'
                    : 'No team members found'
                }
              />
            </div>
            <button
              className={Styles.addButton}
              onClick={handleAddCollaborator}
              disabled={(!selectedMember && (!email || !EMAIL_REGEX.test(email))) || isLoading}
            >
              <FiUserPlus size={16} />
              {isLoading ? 'Adding...' : 'Add'}
            </button>
          </div>

          <div className={Styles.orDivider}>
            <span className={Styles.orText}>OR</span>
          </div>

          <div className={Styles.emailInputWrapper}>
            <input
              type="email"
              className={Styles.emailInput}
              placeholder="Enter email address..."
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setSelectedMember(null);
              }}
              disabled={isLoading}
            />
          </div>

          <p className={Styles.helperText}>
            Add team members or invite via email
          </p>
        </div>

        {/* Collaborators List Section */}
        <div className={Styles.collaboratorsSection}>
          <h3 className={Styles.sectionTitle}>
            People with access ({(campaignOwner ? 1 : 0) + collaborators.filter(c => !c.owner).length})
          </h3>
          <div className={Styles.collaboratorsList}>
            {/* Owner */}
            {campaignOwner && (
              <div className={Styles.collaboratorItem}>
                <div className={Styles.collaboratorInfo}>
                  {campaignOwner.avatar ? (
                    <img
                      src={campaignOwner.avatar}
                      alt={campaignOwner.name}
                      className={Styles.avatar}
                    />
                  ) : (
                    <div className={Styles.avatarPlaceholder}>
                      {getNameInitials(campaignOwner.name)}
                    </div>
                  )}
                  <div className={Styles.collaboratorDetails}>
                    <p className={Styles.collaboratorName}>
                      {campaignOwner.name}
                      {campaignOwner.id === currentUser?.id && ' (You)'}
                    </p>
                    <p className={Styles.collaboratorEmail}>
                      {campaignOwner.email}
                    </p>
                  </div>
                </div>
                <div className={Styles.ownerBadge}>Owner</div>
              </div>
            )}

            {/* Collaborators */}
            {collaborators
              .filter((collab) => !collab.owner)
              .map((collab) => {
                const { id, name, email, avatar } = collab;
                const isJustAdded = justAddedId === id;

                return (
                  <div
                    key={id}
                    className={`${Styles.collaboratorItem} ${isJustAdded ? Styles.justAdded : ''}`}
                  >
                    <div className={Styles.collaboratorInfo}>
                      {avatar ? (
                        <img
                          src={avatar}
                          alt={name}
                          className={Styles.avatar}
                        />
                      ) : (
                        <div className={Styles.avatarPlaceholder}>
                          {getNameInitials(name)}
                        </div>
                      )}
                      <div className={Styles.collaboratorDetails}>
                        <p className={Styles.collaboratorName}>{name}</p>
                        <p className={Styles.collaboratorEmail}>{email}</p>
                      </div>
                    </div>
                    {campaignOwner?.id === currentUser?.id && (
                      <button
                        className={Styles.removeButton}
                        onClick={() => handleRemoveCollaborator(id)}
                        title="Remove access from this campaign"
                      >
                        Remove
                      </button>
                    )}
                    {isJustAdded && (
                      <span className={Styles.newBadge}>New</span>
                    )}
                  </div>
                );
              })}

            {collaborators.filter((c) => !c.owner).length === 0 && campaignOwner && (
              <div className={Styles.emptyState}>
                <p>No additional collaborators</p>
                <span>Add team members to share this campaign</span>
              </div>
            )}

            {!campaignOwner && collaborators.length === 0 && (
              <div className={Styles.emptyState}>
                <p>No one has access yet</p>
                <span>Loading campaign information...</span>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className={Styles.modalFooter}>
        <button className={Styles.doneButton} onClick={props.onHide}>
          Done
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CampaignShareModal;
